import type { ErrorLike, OperationVariables } from '@apollo/client';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { useMutation as useApolloMutation } from '@apollo/client/react';
import type { MutationHookOptions } from '@apollo/client/react';
import type { DocumentNode, GraphQLFormattedError } from 'graphql';
import { toCamel } from 'utils/utils';

export declare type EctorErrorType = {
  code: string;
  message?: string;
  data?: { [key: string]: any };
  errors?: { code: string; key: string; message: string }[];
};

type BaseMutationTuple<TData, TVariables extends OperationVariables> = ReturnType<
  typeof useApolloMutation<TData, TVariables>
>;
type MutationTuple<TData, TVariables extends OperationVariables> = [
  BaseMutationTuple<TData, TVariables>[0],
  BaseMutationTuple<TData, TVariables>[1] & { errors: EctorErrorType[] },
];

export const defaultErrorCode = 'defaultError';

export function useMutation<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  mutation: DocumentNode,
  options?: MutationHookOptions<TData, TVariables>,
  onErrorArg?: (errors: EctorErrorType[]) => void,
): MutationTuple<TData, TVariables> {
  const toEctorError = (error: GraphQLFormattedError): EctorErrorType => {
    const extensions = error.extensions as Record<string, any> | undefined;

    if (extensions) {
      return {
        code: toCamel(extensions.code as string),
        data: extensions.exception,
        message: error.message,
        errors: [],
      };
    }

    try {
      return JSON.parse(error.message) as EctorErrorType;
    } catch (e) {
      return {
        code: defaultErrorCode,
        message: '',
        errors: [],
      };
    }
  };

  const buildErrors = (error: ErrorLike): EctorErrorType[] => {
    if (CombinedGraphQLErrors.is(error)) {
      return error.errors.map(toEctorError);
    }

    if (ServerError.is(error)) {
      return [
        {
          code: defaultErrorCode,
          message: '',
          errors: [],
        },
      ];
    }

    return [];
  };

  const onError = (apolloError: ErrorLike) => {
    if (!onErrorArg) {
      return;
    }
    onErrorArg(buildErrors(apolloError));
  };
  const [mutateFunction, mutateResult] = useApolloMutation<TData, TVariables>(mutation, {
    ...options,
    onError,
  });
  const { error } = mutateResult;
  const errors = error ? buildErrors(error) : [];

  return [mutateFunction, { ...mutateResult, errors }] as MutationTuple<TData, TVariables>;
}
